// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'

// ** Child Imports
import Loader from '../../common/loader-components/Loader'
import { WidgetType } from 'src/lib/types/dashboardAnalytics'
import { Button, Grid, Typography } from '@mui/material'
import { appliedDateFilter, arrayToCsv, downloadCsvHelper, numberWithCommas } from 'src/lib/utils/helpers'
import { funnelEvents } from 'src/assets/AnalyticsConfig'
import { eventNameMapper } from 'src/lib/utils/analyticsHelpers'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { ArrowDownRight, ArrowUpRight, Download, Minus, TrendingDown, TrendingUp } from 'react-feather'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import GenericTable from 'src/components/common/table/GenericTable'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'

const ConversionFunnel: React.FC<WidgetType> = ({
  value,
  isLoading,
  triggerDownloadReport,
  downloadTriggered,
  moreIsGood,
  hasComparison
}) => {
  const { analyticsFilters } = useFilter()
  const { sendActivityLog } = useActivityLogs()
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5
  })
  const modifiedValue = funnelEvents
    ?.map((event: string, index: number) => {
      return value?.find((row: any, index: number) => row?.eventName === event)
    })
    .filter((value: any) => typeof value !== 'undefined')

  useEffect(() => {
    if (downloadTriggered) {
      downloadCsvHelper(arrayToCsv(value, ['Event', 'Sessions', 'Percentage']), 'conversion_funnel_report.csv')
      typeof triggerDownloadReport !== 'undefined' && triggerDownloadReport(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadTriggered])

  const columns: ColumnDef<any>[] = [
    {
      id: 'eventName',
      // flex: 2,
      // sortable: false,
      cell: ({ row }) => {
        // const row = params?.row
        const value = row.original['eventName']
        const sececondayValue = row.original['sessions']

        return (
          <div className='flex flex-col w-52'>
            <Typography
              variant='body2'
              sx={{ color: 'text.primary', lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {eventNameMapper(value)}
            </Typography>
            <Typography variant='body2' sx={{ color: '#949494', lineHeight: '18px' }}>
              {numberWithCommas(sececondayValue, 0)} sessions
            </Typography>
          </div>
        )
      }
    },
    {
      id: 'percentage',
      // flex: 0.7,
      // sortable: false,
      cell: ({ row }) => {
        // const row = params?.row
        const value = row.original['percentage']
        return (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'end',
              margin: 0,
              fontWeight: 500,
              color: '#4D4D4D'
            }}
          >
            <span>{numberWithCommas(value, 2)}%</span>
          </p>
        )
      }
    },
    {
      id: 'compareToVal',
      maxSize: hasComparison ? 70 : 0,
      minSize: hasComparison ? 70 : 0,
      // sortable: false,
      cell: ({ row }) => {
        // const row = params?.row
        const value = row.original['compareToVal']
        const isGood = moreIsGood ? Boolean(parseInt(value) > 0) : Boolean(parseInt(value) < 0)

        return hasComparison ? (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              margin: 0,
              gap: 2,
              fontWeight: 600,
              color: isGood ? '#2C874A' : '#CB2711'
            }}
          >
            {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
              isGood ? (
                <ArrowUpRight className='h-4 w-4 stroke-[2.5px]' />
              ) : (
                <ArrowDownRight className='h-4 w-4 stroke-[2.5px]' />
              )
            ) : (
              <></>
            )}
            {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
              <> {Math.sign(parseInt(value)) === -1 ? value?.slice(1) : value}</>
            ) : (
              <Minus className='text-[#949494] w-[14px] stroke-[2.5px]' />
            )}
          </p>
        ) : (
          <></>
        )
      }
    }
  ]

  return (
    <div className='relative w-full'>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          width: '100%',
          justifyContent: 'end',
          position: 'absolute',
          top: -50,
          right: 0
        }}
      >
        <button
          className='flex items-center h-8 gap-1 px-3 py-2 text-xs font-semibold transition duration-200 bg-white border rounded whitespace-nowrap border-gray-light hover:border-coal-lighter'
          disabled={!value?.length}
          onClick={() => {
            downloadCsvHelper(arrayToCsv(value, ['Event', 'Sessions', 'Percentage']), 'conversion_funnel_report.csv')
            sendActivityLog({
              entityName: EntityName.OVERVIEW,
              actionName: 'downloaded report'
            })
          }}
        >
          <Download className='w-4 h-4' />
        </button>
      </div>
      <GenericTable
        data={modifiedValue ?? []}
        columns={columns}
        pagination={{
          paginationState: pagination,
          setPaginationState: setPagination
        }}
        totalRows={5}
        loading={isLoading}
        customPaginationConfig={{
          customPagination: true
        }}
        isCustomizable={{
          isFullWidth: false,
          isResizable: false,
          hideDropDown: true,
          hidePaginationButton: true,
          hideHeaders: true
        }}
      />
    </div>
  )
}

export default ConversionFunnel
